import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";

const LETTERS: string[] = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode("A".charCodeAt(0) + i),
);

interface ISupersetPickerProps {
  isOpen: boolean;
  current: string | null;
  onClose: () => void;
  onPick: (letter: string | null) => void;
}

/**
 * Bottom-sheet picker for the per-exercise superset letter. Same visual
 * footprint as the rest-timer picker so the two affordances feel like part of
 * the same family.
 */
export const SupersetPicker = ({
  isOpen,
  current,
  onClose,
  onPick,
}: ISupersetPickerProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.rest_picker_backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={styles.rest_picker_sheet}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Избери буква за суперсет"
          >
            <div className={styles.rest_picker_title}>Суперсет</div>
            <div className={styles.rest_picker_sub}>
              Групирай упражненията с обща буква (A1, A2, ...). Сетовете
              получават префикс с буквата.
            </div>

            <div className={styles.superset_picker_grid}>
              <button
                type="button"
                className={classNames(styles.rest_picker_chip, styles.rest_picker_chip_off, {
                  [styles.rest_picker_chip_active]: current === null,
                })}
                onClick={() => {
                  onPick(null);
                  onClose();
                }}
              >
                Без
              </button>
              {LETTERS.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  className={classNames(styles.rest_picker_chip, {
                    [styles.rest_picker_chip_active]: letter === current,
                  })}
                  onClick={() => {
                    onPick(letter);
                    onClose();
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
